import {
    Alert,
    Button,
    Card,
    Group,
    List,
    Loader,
    ScrollArea,
    Stack,
    Table,
    Text,
    Title,
} from '@mantine/core'
import {notifications} from '@mantine/notifications'
import {IconArrowLeft, IconDownload, IconUpload, IconWorld} from '@tabler/icons-react'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {createFileRoute, useParams} from '@tanstack/react-router'
import {useRef, useState} from 'react'

import {
    getOrganizationDetail,
    getUploadTemplateHeaders,
    listUploadEvents,
    previewProspectsCsv,
    UploadError,
    uploadProspects,
} from 'src/api/admin/api'
import type {CsvPreviewResponse} from 'src/api/admin/models/organization'
import LinkButton from 'src/components/ui/link-button'

type HeaderMismatchError = {
    detail: string
    error_type: 'header_mismatch'
    missing_columns: {name: string; required: boolean}[]
    unrecognized_columns: string[]
}

export const Route = createFileRoute('/_authed/admin/organizations/$orgId/data')({
    component: AdminOrganizationDataPage,
})

function AdminOrganizationDataPage() {
    const {orgId} = useParams({from: '/_authed/admin/organizations/$orgId/data'})
    const queryClient = useQueryClient()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [preview, setPreview] = useState<CsvPreviewResponse | null>(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [uploadError, setUploadError] = useState<HeaderMismatchError | null>(null)

    const {data: org, isLoading: orgLoading} = useQuery({
        queryKey: ['admin', 'organizations', orgId],
        queryFn: () => getOrganizationDetail(orgId),
    })

    const {data: uploads = [], isLoading: uploadsLoading} = useQuery({
        queryKey: ['admin', 'organizations', orgId, 'uploads'],
        queryFn: () => listUploadEvents(orgId),
    })

    async function handleDownloadTemplate() {
        try {
            const {headers} = await getUploadTemplateHeaders()
            const escape = (s: string) => `"${s.replace(/"/g, '""')}"`
            const csv = headers.map(escape).join(',') + '\n'
            const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'})
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'prospect-upload-template.csv'
            a.click()
            URL.revokeObjectURL(url)
        } catch {
            notifications.show({
                color: 'red',
                message: 'Failed to download template',
            })
        }
    }

    async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0]
        if (!file) return
        setUploadError(null)
        setPreview(null)
        setSelectedFile(file)
        setPreviewLoading(true)
        try {
            const data = await previewProspectsCsv(orgId, file)
            setPreview(data)
        } catch (err) {
            setSelectedFile(null)
            if (
                err instanceof UploadError &&
                err.body &&
                typeof err.body === 'object' &&
                (err.body as HeaderMismatchError).error_type === 'header_mismatch'
            ) {
                setUploadError(err.body as HeaderMismatchError)
                return
            }
            notifications.show({
                color: 'red',
                message: err instanceof Error ? err.message : 'Preview failed',
            })
        } finally {
            setPreviewLoading(false)
            e.target.value = ''
        }
    }

    function handleCancelPreview() {
        setSelectedFile(null)
        setPreview(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    async function handleConfirmUpload() {
        if (!selectedFile) return
        setUploading(true)
        try {
            const data = await uploadProspects(orgId, selectedFile)
            setSelectedFile(null)
            setPreview(null)
            queryClient.invalidateQueries({
                queryKey: ['admin', 'organizations', orgId],
            })
            queryClient.invalidateQueries({
                queryKey: ['admin', 'organizations', orgId, 'uploads'],
            })
            notifications.show({
                color: 'green',
                message: `Uploaded ${data.recordCount} records`,
            })
        } catch (err) {
            if (
                err instanceof UploadError &&
                err.body &&
                typeof err.body === 'object' &&
                (err.body as HeaderMismatchError).error_type === 'header_mismatch'
            ) {
                setUploadError(err.body as HeaderMismatchError)
                return
            }
            notifications.show({
                color: 'red',
                message: err instanceof Error ? err.message : 'Upload failed',
            })
        } finally {
            setUploading(false)
        }
    }

    if (orgLoading || !org) {
        return <Loader size="lg" style={{display: 'block', margin: '100px auto'}} />
    }

    return (
        <Stack gap="lg">
            <Group justify="space-between">
                <Title order={3}>{org.name} - Data Management</Title>
                <Group>
                    <LinkButton
                        to="/admin/organizations/$orgId"
                        params={{orgId}}
                        variant="default"
                        leftSection={<IconArrowLeft size={16} />}
                    >
                        Back to Organization Details
                    </LinkButton>
                    <LinkButton
                        to="/organizations/$orgId"
                        params={{orgId}}
                        variant="default"
                        leftSection={<IconWorld size={16} />}
                    >
                        View as Client
                    </LinkButton>
                </Group>
            </Group>

            <Card withBorder padding="md">
                <Card.Section withBorder inheritPadding py="sm">
                    <Text fw={500}>Prospect Records</Text>
                </Card.Section>
                <Card.Section inheritPadding pt="sm" pb="sm">
                    <Text size="lg">{org.prospectCount}</Text>
                </Card.Section>
            </Card>

            <Card withBorder padding="md">
                <Card.Section withBorder inheritPadding py="sm">
                    <Text fw={500}>Upload Prospect List</Text>
                </Card.Section>
                <Card.Section inheritPadding pt="sm" pb="sm">
                    <Stack gap="sm">
                        <Text size="sm" c="dimmed">
                            Upload a CSV file in the same format as the template file.
                        </Text>
                        <Alert color="yellow">
                            <Text>
                                <Text span fw={500}>
                                    Note:
                                </Text>{' '}
                                This will replace all existing prospect records for the
                                organization.
                            </Text>
                        </Alert>
                        <Group>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv"
                                style={{display: 'none'}}
                                onChange={handleFileSelect}
                            />
                            <Button
                                leftSection={<IconUpload size={16} />}
                                loading={previewLoading}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Select CSV
                            </Button>
                        </Group>
                        {preview && (
                            <Stack gap="sm">
                                <Text fw={500}>
                                    {preview.recordCount} prospect record
                                    {preview.recordCount !== 1 ? 's' : ''} found. Here's a preview:
                                </Text>
                                <ScrollArea.Autosize
                                    mah={400}
                                    type="scroll"
                                    bd="1px solid var(--mantine-color-gray-2)"
                                    scrollbars="xy"
                                >
                                    <ScrollArea w="100%" type="scroll">
                                        <Table withTableBorder withColumnBorders>
                                            <Table.Thead>
                                                <Table.Tr>
                                                    {(preview.previewRows[0] ?? []).map(
                                                        ([header], colIdx) => (
                                                            <Table.Th
                                                                key={`${header}-${colIdx}`}
                                                                style={
                                                                    preview.unrecognizedColumns.includes(
                                                                        header,
                                                                    )
                                                                        ? {
                                                                              backgroundColor:
                                                                                  'var(--mantine-color-gray-light)',
                                                                              color: 'var(--mantine-color-dimmed)',
                                                                          }
                                                                        : undefined
                                                                }
                                                            >
                                                                {header}
                                                            </Table.Th>
                                                        ),
                                                    )}
                                                </Table.Tr>
                                            </Table.Thead>
                                            <Table.Tbody>
                                                {preview.previewRows.map((row, i) => (
                                                    <Table.Tr key={i}>
                                                        {row.map(([header, value], colIdx) => (
                                                            <Table.Td
                                                                key={`${header}-${colIdx}`}
                                                                maw={200}
                                                                style={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    ...(preview.unrecognizedColumns.includes(
                                                                        header,
                                                                    )
                                                                        ? {
                                                                              backgroundColor:
                                                                                  'var(--mantine-color-gray-light)',
                                                                              color: 'var(--mantine-color-dimmed)',
                                                                          }
                                                                        : {}),
                                                                }}
                                                            >
                                                                {value}
                                                            </Table.Td>
                                                        ))}
                                                    </Table.Tr>
                                                ))}
                                            </Table.Tbody>
                                        </Table>
                                    </ScrollArea>
                                </ScrollArea.Autosize>
                                <Group>
                                    <Button
                                        color="green"
                                        loading={uploading}
                                        onClick={handleConfirmUpload}
                                    >
                                        Confirm Upload
                                    </Button>
                                    <Button
                                        variant="default"
                                        disabled={uploading}
                                        onClick={handleCancelPreview}
                                    >
                                        Cancel
                                    </Button>
                                </Group>
                            </Stack>
                        )}
                        {uploadError && (
                            <Alert
                                color="red"
                                variant="light"
                                title={
                                    <Text fw={700} tt="uppercase">
                                        Column names don't match
                                    </Text>
                                }
                                withCloseButton
                                closeButtonLabel="Dismiss"
                                onClose={() => setUploadError(null)}
                            >
                                <Stack gap="md">
                                    {uploadError.missing_columns.length > 0 ? (
                                        <Stack gap="xs">
                                            <Text size="md" fw={500}>
                                                Missing expected columns
                                            </Text>
                                            <Text size="sm" c="dimmed">
                                                These columns were expected but not found in the
                                                upload. Required columns (highlighted) must be
                                                present for the upload to succeed.
                                            </Text>
                                            <List size="sm">
                                                {uploadError.missing_columns.map((col) => (
                                                    <List.Item key={col.name}>
                                                        <Text
                                                            span
                                                            size="sm"
                                                            fw={col.required ? 600 : 400}
                                                            c={
                                                                col.required
                                                                    ? 'var(--mantine-color-red-7)'
                                                                    : undefined
                                                            }
                                                        >
                                                            {col.name}
                                                        </Text>
                                                        {col.required && (
                                                            <Text span size="sm" c="dimmed">
                                                                {' '}
                                                                (required)
                                                            </Text>
                                                        )}
                                                    </List.Item>
                                                ))}
                                            </List>
                                        </Stack>
                                    ) : (
                                        <Text size="md">Error message: {uploadError.detail}</Text>
                                    )}
                                    {uploadError.unrecognized_columns.length > 0 && (
                                        <Stack gap="xs">
                                            <Text size="md" fw={500}>
                                                Unrecognized columns
                                            </Text>
                                            <Text size="sm" c="dimmed">
                                                These columns were found in the upload but are not
                                                recognized. Check to see if any of these columns are
                                                typed differently than the missing column names
                                                above.
                                            </Text>
                                            <List size="sm">
                                                {uploadError.unrecognized_columns.map((name) => (
                                                    <List.Item key={name}>{name}</List.Item>
                                                ))}
                                            </List>
                                        </Stack>
                                    )}
                                </Stack>
                            </Alert>
                        )}
                    </Stack>
                </Card.Section>
            </Card>

            <Card withBorder padding="md">
                <Card.Section withBorder inheritPadding py="sm">
                    <Text fw={500}>Upload Timeline</Text>
                </Card.Section>
                <Card.Section inheritPadding pt="sm" pb="sm">
                    {uploadsLoading ? (
                        <Loader size="sm" />
                    ) : uploads.length === 0 ? (
                        <Text c="dimmed" size="sm">
                            No uploads yet
                        </Text>
                    ) : (
                        <Table>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>File</Table.Th>
                                    <Table.Th>Records</Table.Th>
                                    <Table.Th>Uploaded by</Table.Th>
                                    <Table.Th>Date</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {uploads.map((ev) => (
                                    <Table.Tr key={ev.id}>
                                        <Table.Td>{ev.fileName}</Table.Td>
                                        <Table.Td>{ev.recordCount}</Table.Td>
                                        <Table.Td>{ev.uploadedByName}</Table.Td>
                                        <Table.Td>
                                            {new Date(ev.createdAt).toLocaleString()}
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    )}
                </Card.Section>
            </Card>

            <Card withBorder padding="md">
                <Card.Section withBorder inheritPadding py="sm">
                    <Text fw={500}>File Format Template</Text>
                </Card.Section>
                <Card.Section inheritPadding pt="sm" pb="sm">
                    <Stack gap="sm">
                        <Text size="sm" c="dimmed">
                            Download a CSV with the expected column headers. Open it in Excel, add
                            your prospect data in the same columns, then save as CSV and upload
                            above. Using this template ensures your file matches the expected
                            format.
                        </Text>
                        <Group>
                            <Button
                                variant="default"
                                leftSection={<IconDownload size={16} />}
                                onClick={handleDownloadTemplate}
                            >
                                Download Template
                            </Button>
                        </Group>
                    </Stack>
                </Card.Section>
            </Card>
        </Stack>
    )
}
